import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";
import { Eye, Users, Layers, CalendarCheck, LogOut, Smartphone, RefreshCw, Pencil, Settings } from "lucide-react";

const GOLD = "#BE924B";
const PIE_COLORS = ["#BE924B", "#8a6b3a", "#D9C2A0", "#5c5148", "#e0cbab"];

const RANGES = [
  { key: "7d", label: "7 Days", gran: "day" },
  { key: "30d", label: "30 Days", gran: "day" },
  { key: "90d", label: "90 Days", gran: "week" },
  { key: "365d", label: "12 Months", gran: "month" },
];

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="soft-card rounded-2xl p-6 bg-white">
      <div className="flex items-center justify-between">
        <p className="eyebrow">{label}</p>
        <span className="w-10 h-10 rounded-full bg-[var(--cream-2)] flex items-center justify-center text-[var(--gold)]">
          <Icon size={18} />
        </span>
      </div>
      <p className="font-display text-5xl text-[var(--charcoal)] mt-3">{value}</p>
    </div>
  );
}

function Panel({ title, children, testid }) {
  return (
    <div data-testid={testid} className="soft-card rounded-2xl p-6 bg-white">
      <h3 className="font-display text-2xl text-[var(--charcoal)] mb-5">{title}</h3>
      {children}
    </div>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [range, setRange] = useState(RANGES[1]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async (r) => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get(`/analytics/summary?range=${r.key}&granularity=${r.gran}`);
      setData(res.data);
    } catch (e) {
      setError("Couldn't load analytics. Please refresh or sign in again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(range); /* eslint-disable-next-line */ }, [range]);

  const totals = data?.totals || {};
  const hasData = (totals.views || 0) > 0;

  const devices = useMemo(
    () => (data?.devices || []).map((d) => ({ ...d, name: d.name.charAt(0).toUpperCase() + d.name.slice(1) })),
    [data]
  );

  return (
    <div className="min-h-screen bg-[var(--cream)]">
      <header className="bg-[var(--charcoal)] text-[#F3E9DC]">
        <div className="max-w-[1300px] mx-auto px-5 lg:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/assets/seraphina-logo-hero.png" alt="Seraphina" className="h-9 w-9 object-contain" />
            <div>
              <h1 className="font-display text-2xl leading-none">Analytics Dashboard</h1>
              <p className="text-[#c9bba9] text-xs mt-1">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin/content" data-testid="nav-edit-site" className="flex items-center gap-2 text-sm text-[#F3E9DC] hover:text-[var(--gold-soft)] transition-colors">
              <Pencil size={15} /> Edit Site
            </Link>
            <Link to="/admin/account" data-testid="nav-account" className="flex items-center gap-2 text-sm text-[#F3E9DC] hover:text-[var(--gold-soft)] transition-colors">
              <Settings size={15} /> Account
            </Link>
            <button data-testid="refresh-btn" onClick={() => load(range)} className="flex items-center gap-2 text-sm text-[#F3E9DC] hover:text-[var(--gold-soft)] transition-colors">
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button data-testid="logout-btn" onClick={logout} className="btn-gold rounded-full px-5 py-2.5 flex items-center gap-2">
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1300px] mx-auto px-5 lg:px-10 py-8">
        {/* Range selector */}
        <div className="flex flex-wrap gap-2 mb-8">
          {RANGES.map((r) => (
            <button
              key={r.key}
              data-testid={`range-${r.key}`}
              onClick={() => setRange(r)}
              className={`rounded-full px-5 py-2 text-sm tracking-wide transition-colors border ${
                range.key === r.key
                  ? "bg-[var(--gold)] text-white border-[var(--gold)]"
                  : "bg-white text-[var(--charcoal)] border-[var(--line)] hover:border-[var(--gold)]"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>

        {error && (
          <div data-testid="dashboard-error" className="soft-card rounded-2xl p-5 mt-6 bg-white border-red-200 text-red-600 flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => load(range)} className="btn-outline-gold rounded-full px-5 py-2">Retry</button>
          </div>
        )}

        {/* Stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-5">
          <StatCard testid="stat-visitors" icon={Users} label="Visitors" value={totals.visitors ?? 0} />
          <StatCard testid="stat-views" icon={Eye} label="Page Views" value={totals.views ?? 0} />
          <StatCard testid="stat-sessions" icon={Layers} label="Sessions" value={totals.sessions ?? 0} />
          <StatCard testid="stat-book" icon={CalendarCheck} label="Book Clicks" value={totals.book_clicks ?? 0} />
          <StatCard testid="stat-service" icon={Smartphone} label="Service Clicks" value={totals.service_clicks ?? 0} />
        </div>

        {!hasData && !loading && (
          <div data-testid="no-data" className="soft-card rounded-2xl p-10 mt-8 text-center bg-white">
            <p className="font-display text-3xl text-[var(--charcoal)]">No traffic yet</p>
            <p className="text-[var(--charcoal-soft)] mt-2">Visits will appear here as people browse the site.</p>
          </div>
        )}

        {/* Traffic over time */}
        <div className="mt-8">
          <Panel testid="chart-traffic" title="Visitors & Page Views Over Time">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={data?.timeseries || []}>
                <defs>
                  <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={GOLD} stopOpacity={0.35} />
                    <stop offset="95%" stopColor={GOLD} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gVis" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#5c5148" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#5c5148" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eadfce" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#6B645C" }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#6B645C" }} />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="views" name="Page Views" stroke={GOLD} fill="url(#gViews)" strokeWidth={2} />
                <Area type="monotone" dataKey="visitors" name="Visitors" stroke="#5c5148" fill="url(#gVis)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </Panel>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mt-6">
          {/* Top pages */}
          <Panel testid="panel-top-pages" title="Most-Visited Pages">
            <div className="space-y-3">
              {(data?.top_pages || []).map((p) => (
                <div key={p.path} className="flex items-center justify-between text-sm">
                  <span className="text-[var(--charcoal)] truncate">{p.path === "/" ? "/ (Home)" : p.path}</span>
                  <span className="font-medium text-[var(--gold)]">{p.views}</span>
                </div>
              ))}
              {(!data?.top_pages || data.top_pages.length === 0) && <p className="text-sm text-[var(--charcoal-soft)]">No data yet.</p>}
            </div>
          </Panel>

          {/* Devices */}
          <Panel testid="panel-devices" title="Device Type">
            {devices.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={devices} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85} label>
                    {devices.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-[var(--charcoal-soft)]">No data yet.</p>}
          </Panel>

          {/* Service clicks */}
          <Panel testid="panel-services" title="Service Clicks">
            {(data?.services || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.services} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eadfce" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "#6B645C" }} />
                  <YAxis type="category" dataKey="label" width={130} tick={{ fontSize: 10, fill: "#6B645C" }} />
                  <Tooltip />
                  <Bar dataKey="clicks" fill={GOLD} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-[var(--charcoal-soft)]">No service clicks yet.</p>}
          </Panel>

          {/* Book clicks by label */}
          <Panel testid="panel-book" title="Book Now Clicks by Location">
            <div className="space-y-3">
              {(data?.book_clicks_by_label || []).map((b) => (
                <div key={b.label} className="flex items-center justify-between text-sm">
                  <span className="text-[var(--charcoal)]">{b.label}</span>
                  <span className="font-medium text-[var(--gold)]">{b.clicks}</span>
                </div>
              ))}
              {(!data?.book_clicks_by_label || data.book_clicks_by_label.length === 0) && <p className="text-sm text-[var(--charcoal-soft)]">No booking clicks yet.</p>}
            </div>
          </Panel>
        </div>

        {/* Traffic sources + Locations */}
        <div className="grid lg:grid-cols-2 gap-6 mt-6">
          <Panel testid="panel-sources" title="Traffic Sources — How Visitors Arrived">
            <div className="space-y-3">
              {(data?.sources || []).map((s) => {
                const max = Math.max(...(data.sources || []).map((x) => x.visits), 1);
                return (
                  <div key={s.source} className="text-sm">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[var(--charcoal)]">{s.source}</span>
                      <span className="font-medium text-[var(--gold)]">{s.visits} {s.visits === 1 ? "visit" : "visits"}</span>
                    </div>
                    <div className="h-2 rounded-full bg-[var(--cream-2)] overflow-hidden">
                      <div className="h-full bg-[var(--gold)]" style={{ width: `${(s.visits / max) * 100}%` }} />
                    </div>
                  </div>
                );
              })}
              {(!data?.sources || data.sources.length === 0) && <p className="text-sm text-[var(--charcoal-soft)]">No source data yet.</p>}
              <p className="text-[0.7rem] text-[var(--charcoal-soft)] pt-2 leading-relaxed">
                "Direct" = typed the address or used a bookmark. Google, Facebook, Instagram, etc. show which channel referred each visit.
              </p>
            </div>
          </Panel>

          <Panel testid="panel-locations" title="Visitor Locations">
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
              {(data?.locations || []).map((l, i) => (
                <div key={i} className="flex items-center justify-between text-sm border-b border-[var(--line)] pb-2">
                  <span className="text-[var(--charcoal)]">{l.city}, {l.country}</span>
                  <span className="font-medium text-[var(--gold)]">{l.views}</span>
                </div>
              ))}
              {(!data?.locations || data.locations.length === 0) && <p className="text-sm text-[var(--charcoal-soft)]">No location data yet.</p>}
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}
